import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { ERROR_NO_PRINTER_SELECTED } from 'my-phorest/services/electron-app';
import { task } from 'ember-concurrency';
import GenerateA4TravelCards from 'my-phorest/gql/mutations/generate-travel-cards.graphql';

import { queryManager } from 'ember-apollo-client';

const IFRAME_ID_PRINT = 'print-travel-card-iframe';

export default class ClientTravelCardPrinterService extends Service {
  @service session;
  @service electronApp;
  @service electronAppPrinter;
  @service router;
  @service('browser/navigator') navigator;
  @service('browser/document') document;
  @service backgroundTasks;
  @service notifications;
  @service intl;

  @queryManager apollo;

  @tracked isPrinting = false;
  @tracked isBrowserPrintOpen = false;
  @tracked appointmentId;

  async print(appointmentId) {
    this.isPrinting = true;
    this.appointmentId = appointmentId;

    if (this.electronApp.isRunningInElectron) {
      try {
        await this.electronAppPrinter.printClientTravelCardTask.perform(
          appointmentId
        );
      } catch (error) {
        // Ignore the error if no printer is selected through the first time print selection dialog.
        if (error?.code !== ERROR_NO_PRINTER_SELECTED) throw error;
      } finally {
        this.isPrinting = false;
      }
    } else {
      this.isBrowserPrintOpen = true;
    }
  }

  @action
  onDone() {
    this.isPrinting = false;
    this.isBrowserPrintOpen = false;
  }

  @task
  *executeMutation(clientIds, type) {
    return yield this.apollo.mutate(
      {
        mutation: GenerateA4TravelCards,
        variables: {
          clientIds,
          type,
        },
      },
      'generateTravelCards'
    );
  }

  @task
  *scheduleA4PrintTask(clientIds, type) {
    let response = yield this.executeMutation.perform(clientIds, type);
    let taskId = response.taskId;
    if (taskId) {
      let notification = this.notifications.info(
        this.intl.t('appointments.print.travel-card-modal.in-progress.title'),
        this.intl.t(
          'appointments.print.travel-card-modal.in-progress.description'
        ),
        { sticky: true, showSpinner: true }
      );
      this.backgroundTasks.subscribe(taskId, {
        onComplete: (resource) =>
          this.onCompleteTravelCardA4Job(resource, notification),
        onFail: () => this.onFailTravelCardA4Job(notification),
      });
    }
  }

  async onCompleteTravelCardA4Job(resource, notification) {
    await this.showPrintFromUrl(resource.documentUrl);
    this.removeNotification(notification);
  }

  onFailTravelCardA4Job(notification) {
    this.notifications.failure(
      this.intl.t('global.error-notification-title'),
      this.intl.t(
        'appointments.print.travel-card-modal.error-during-generation'
      ),
      { sticky: true }
    );
    this.removeNotification(notification);
  }

  removeNotification(notification) {
    if (notification) {
      this.notifications.removeNotification.perform(notification);
    }
  }

  showPrintFromUrl(url) {
    let isSafari = this.navigator.userAgent.includes('Safari');
    let iframe = this.document.getElementById(IFRAME_ID_PRINT);
    if (!iframe) {
      iframe = this.document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.id = IFRAME_ID_PRINT;
      this.document.body.appendChild(iframe);
    }

    return fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        iframe.src = blobUrl;
        iframe.onload = () => {
          if (isSafari) {
            iframe.contentWindow.requestAnimationFrame(() => {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            });
          } else {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
          URL.revokeObjectURL(blobUrl);
        };
      });
  }

  @action
  removeIframeFromDOM() {
    let iframe = this.document.getElementById(IFRAME_ID_PRINT);
    iframe?.remove();
  }
}
